<script lang="ts">
import { computed, defineComponent, inject, onActivated, onDeactivated, PropType, ref, watch } from "vue";
import Steps from "@/components/Steps.vue";
import ValidierungsFehler from "@/components/ValidierungsFehler.vue";
import InfoBox from "@/components/grid/InfoBox.vue";
import Infotext from "@/components/Infotext.vue";
import formValidierung from "@/businesslogic/formValidierung";
import Ueberschrift from "@/components/grid/Ueberschrift.vue";
import Zeile from "@/components/grid/Zeile.vue";
import Zelle from "@/components/grid/Zelle.vue";
import Modal from "@/components/Modal.vue";
import formatUtils from "@/businesslogic/utils/formatUtils";
import TitelZelle from "@/components/grid/TitelZelle.vue";
import { NavvItem } from "@/model/NavItem";
import IconSprite from "@/components/IconSprite.vue";
import Logger from "js-logger";
import Adresse from "@/components/person/Adresse.vue";
import berechnungUtils from "@/businesslogic/utils/berechnungUtils";
import GebaeudeTypRadio from "@/components/gebaeudetypen/GebaeudeTypRadio.vue";
import Gebaeudetypen from "@/components/gebaeudetypen/GeabeudetypenVHV2024";
import dataUtils from "@/businesslogic/utils/dataUtils";
import { useFormStore } from "@/businesslogic/stores/formStore";

export default defineComponent({
  name: "BasisdatenVHV2024",
  emits: ["prev-page", "next-page", "goto-page", "handle-error"],
  components: {
    TitelZelle,
    Zelle,
    Ueberschrift,
    Infotext,
    InfoBox,
    ValidierungsFehler,
    Steps,
    Zeile,
    Modal,
    IconSprite,
    Adresse,
    GebaeudeTypRadio,
  },
  props: {
    navItems: Object as PropType<NavvItem[]>,
    anfrageId: String,
  },
  setup(props, { emit }) {
    const piniaStore = useFormStore();
    const fehler = ref({} as any);
    const istSichtbar: any = inject("istSichtbar");
    const wechsleSichtbarkeit: any = inject("wechsleSichtbarkeit");
    const zeigeSpinner: any = inject("zeigeSpinner");
    const { ermittleVersicherungssumme } = berechnungUtils();
    const { formatCurrency } = formatUtils();
    const { gte, lte, gt, lt } = dataUtils();
    let gewaehlteVersicherungssumme: number | null = 0;
    let komponenteIstAktiv = false;

    const gebaeudetypen = ref(Gebaeudetypen);
    const mindestVersicherungssumme = 10000;

    const empfohleneVersicherungssumme = computed(() => {
      if (piniaStore.state?.gebaeudedaten?.wohnflaeche !== undefined) {
        return ermittleVersicherungssumme(piniaStore.state.gebaeudedaten.wohnflaeche);
      }
      return 0;
    });

    // Bei Vorbelegung keine automatische VS-Berechnung (TARGET-2945)
    const versicherungssummeAutomatischBerechnen = computed(() => {
      return (
        piniaStore.state?.vorbelegungsId == "" || piniaStore.state?.tarifinformationen?.versicherungssumme === null
      );
    });

    const sollGebaeudetypAbfragen = computed(() => {
      return gte(piniaStore.state.gebaeudedaten.wohnflaeche, 80);
    });

    const { erzwingeGanzzahl } = formValidierung();

    function grenzwerteUeberschritten(): boolean {
      if (
        !piniaStore.state.isMakler &&
        !piniaStore.state.isMaklerLink &&
        (gt(piniaStore.state.gebaeudedaten.wohnflaeche, 300) ||
          gt(piniaStore.state.tarifinformationen.versicherungssumme, 200000))
      ) {
        wechsleSichtbarkeit("modal-GrenzwerteUeberschritten");
        return true;
      }
      return false;
    }

    const versicherungsSummeBerechnen = () => {
      if (
        versicherungssummeAutomatischBerechnen.value &&
        empfohleneVersicherungssumme.value > 0 &&
        lte(piniaStore.state.gebaeudedaten.wohnflaeche, 3000)
      ) {
        setzeVersicheungssumme(empfohleneVersicherungssumme.value);
        Logger.debug("versicherungsSummeBerechnen(): VS vorbelegt auf " + empfohleneVersicherungssumme.value);
        const vsElement = document.querySelector("#versicherungssumme");
        if (vsElement) {
          // wenn VS neu gesetzt wurde, etwaige Fehlermeldung zur VS durch Neuvalidierung eliminieren
          vsElement.dispatchEvent(new CustomEvent("blur"));
        }
      } else {
        Logger.debug("versicherungsSummeBerechnen(): Automatische Berechnung inaktiv.");
      }
    };

    const versicherungsSummePruefen = () => {
      if (
        piniaStore.state.tarifinformationen.versicherungssumme &&
        piniaStore.state.tarifinformationen.versicherungssumme >= mindestVersicherungssumme &&
        piniaStore.state.tarifinformationen.versicherungssumme < empfohleneVersicherungssumme.value &&
        piniaStore.state.tarifinformationen.versicherungssumme !== gewaehlteVersicherungssumme
      ) {
        wechsleSichtbarkeit("modal-AnpassungVersicherungssumme");
      }
    };

    const empfohleneVersicherungssummeVerwenden = () => {
      setzeVersicheungssumme(empfohleneVersicherungssumme.value);
      wechsleSichtbarkeit("modal-AnpassungVersicherungssumme");
    };

    const gewaehlteVersicherungssummeVerwenden = () => {
      if (piniaStore.state.tarifinformationen.versicherungssumme !== undefined) {
        gewaehlteVersicherungssumme = piniaStore.state.tarifinformationen.versicherungssumme;
      }

      wechsleSichtbarkeit("modal-AnpassungVersicherungssumme");
    };

    const checkPlzValidierung = () => {
      if (!piniaStore.state.plzValidiert) {
        fehler.value["risikoAnschriftPlz"] = "Bitte geben Sie eine gültige deutsche Postleitzahl ein.";
      }
    };

    const eingabenSindValide = () => {
      return new Promise((resolve) => {
        const { validiereForm, getFormFehlerAnzahl, getFormFehler } = formValidierung();
        validiereForm("").then(() => {
          fehler.value = getFormFehler();
          checkPlzValidierung();
          const grenzwertIstUeberschritten = grenzwerteUeberschritten();
          if (!grenzwertIstUeberschritten) {
            versicherungsSummePruefen();
          }

          Logger.debug("Valid", fehler.value, piniaStore.state.plzValidiert);

          resolve(
            getFormFehlerAnzahl() === 0 &&
              piniaStore.state.plzValidiert &&
              !grenzwertIstUeberschritten &&
              !istSichtbar("modal-AnpassungVersicherungssumme"),
          );
        });
      });
    };

    const nextPage = () => {
      zeigeSpinner.value = true;
      eingabenSindValide()
        .then((isValid) => {
          if (isValid) {
            piniaStore.state.personendaten.versicherungsnehmer.adresse.plz = piniaStore.state.risikoAnschrift.plz;
            piniaStore.state.neuberechnen = true;
          }
        })
        .finally(() => {
          zeigeSpinner.value = false;
        });
    };

    watch(
      () => [piniaStore.state.berechnungDurchgefuehrt],
      () => {
        if (piniaStore.state.berechnungDurchgefuehrt && komponenteIstAktiv) {
          piniaStore.state.berechnungDurchgefuehrt = false;
          emit("next-page", { pageIndex: 0 });
        }
      },
    );

    function setzeVersicheungssumme(summe: number) {
      piniaStore.state.tarifinformationen.versicherungssumme = summe;
    }

    watch(
      () => piniaStore.state.gebaeudedaten.wohnflaeche,
      () => {
        versicherungsSummeBerechnen();
        if (lt(piniaStore.state.gebaeudedaten.wohnflaeche, 80)) {
          piniaStore.state.gebaeudedaten.gebaeudetyp = "";
        }
      },
    );

    onActivated(() => {
      komponenteIstAktiv = true;
    });

    onDeactivated(() => {
      komponenteIstAktiv = false;
    });

    return {
      props,
      fehler,
      piniaStore,
      istSichtbar,
      wechsleSichtbarkeit,
      nextPage,
      versicherungsSummeBerechnen,
      empfohleneVersicherungssumme,
      empfohleneVersicherungssummeVerwenden,
      formatCurrency,
      erzwingeGanzzahl,
      gewaehlteVersicherungssummeVerwenden,
      gebaeudetypen,
      sollGebaeudetypAbfragen,
    };
  },
});
</script>

<template>
  <Steps :active="0" v-bind:items="navItems" @goto-page="$emit('goto-page', $event)" />
  <section>
    <ueberschrift titel="Wohnobjekt" />

    <zeile>
      <titel-zelle>Wohnfläche in m²</titel-zelle>
      <zelle :breite="3">
        <input
          v-model.lazy.trim.number="piniaStore.state.gebaeudedaten.wohnflaeche"
          id="wohnflaeche"
          name="wohnflaeche"
          type="number"
          @keydown="erzwingeGanzzahl"
          class="c-form-control__input"
          min="16"
          max="3000"
          required
          aria-required="true"
          aria-label="Wohnfläche in Quadratmetern"
          placeholder="m²"
          autofocus
          data-fehlermeldung="Bitte geben Sie eine Wohnfläche in m² (mind. 16 m²) ohne Punkt und Komma ein."
          data-cy="wohnflaeche"
          @change="versicherungsSummeBerechnen()"
        />
        <validierungs-fehler :fehler-text="fehler.wohnflaeche" />
      </zelle>
    </zeile>

    <adresse
      :fehler="fehler"
      :placeholder="true"
      v-model:plz="piniaStore.state.risikoAnschrift.plz"
      v-model:ort="piniaStore.state.risikoAnschrift.ort"
      v-model:strasse="piniaStore.state.risikoAnschrift.strasse"
      v-model:hausnummer="piniaStore.state.risikoAnschrift.hausnummer"
      :feldNamen="{
        plz: 'risikoPlz',
        ort: 'risikoOrt',
        strasse: 'risikoStrasse',
        hausnummer: 'risikoHausnummer',
      }"
      :sichtbare-felder="['plz']"
      :clear-ort-on-plz-focus="true"
      ref="risikoAdresseRef"
    >
      <template v-slot:plzOrtLabel>
        Postleitzahl des Wohnorts
        <span
          v-if="!piniaStore.state.isMakler"
          class="gev19__contexthelp-open"
          title="Hilfe anzeigen"
          @click="wechsleSichtbarkeit('beratungsAngebotDirekt')"
        />
        <span
          v-else
          class="gev19__contexthelp-open"
          title="Hilfe anzeigen"
          @click="wechsleSichtbarkeit('beratungsAngebotMakler')"
        />
      </template>
    </adresse>

    <gebaeude-typ-radio
      v-if="sollGebaeudetypAbfragen"
      :fehler="fehler"
      :gebaeudetypen="gebaeudetypen"
      :required="sollGebaeudetypAbfragen"
    ></gebaeude-typ-radio>

    <zeile>
      <zelle :breite="6" :offset="4">
        <div v-if="piniaStore.state.isMakler" class="u-copy-s gev19-infotext--grey">
          Die Hausratversicherung gilt für Häuser und Wohnungen, die ständig bewohnt sind und bei denen es sich um
          Massiv- oder Fertighäuser mit hartem Dach handelt. Bei Hausrat in Zweitwohnungen, anderen nicht ständig
          bewohnten Wohnungen oder Häusern sowie Wochenendhäusern sprechen Sie uns gerne an.
        </div>
        <div v-else class="u-copy-s gev19-infotext--grey">
          Unsere Hausratversicherung gilt für Häuser und Wohnungen, die ständig bewohnt sind und bei denen es sich um
          Massiv- oder Fertighäuser mit hartem Dach handelt.
        </div>

        <info-box id="beratungsAngebotMakler">
          <div><strong>Sie benötigen ein individuelles Angebot?</strong></div>
          <p class="u-copy-s">
            Unser Maklervertriebscenter erstellt Ihnen gern ein individuelles Angebot. Sie erreichen uns von Montag bis
            Freitag von 8:00 bis 18:00 Uhr.
          </p>
          <div class="o-grid u-pad-x-1">
            <div class="o-media-object">
              <div class="o-media-object__media">
                <icon-sprite css-klassen="u-color-primary" icon-name="icon-phone" />
              </div>
              <div class="o-media-object__content">
                <span><a class="inherited" href="tel:+49-40-3766-3222">Tel.: 040 3766 3222</a></span>
              </div>
            </div>
            <div class="o-media-object">
              <div class="o-media-object__media">
                <icon-sprite css-klassen="u-color-primary" icon-name="icon-paper-airplane" />
              </div>
              <div class="o-media-object__content">
                <span
                  ><a class="inherited" href="mailto:maklerservice@grundvers.de">maklerservice@grundvers.de</a></span
                >
              </div>
            </div>
          </div>
        </info-box>

        <info-box id="beratungsAngebotDirekt">
          <div><strong>Sie wohnen in einem anderen Haus?</strong></div>
          <p class="u-copy-s">
            Unser Beratungsteam erstellt Ihnen gern ein individuelles Angebot. Sie erreichen uns von Montag bis Freitag
            von 8:00 bis 18:00 Uhr.
          </p>
          <div class="o-grid u-pad-x-1">
            <div class="o-media-object">
              <div class="o-media-object__media">
                <icon-sprite css-klassen="u-color-primary" icon-name="icon-phone" />
              </div>
              <div class="o-media-object__content">
                <span><a class="inherited" href="tel:+49-40-3766-3366">Tel.: 040 3766 3366</a></span>
              </div>
            </div>
            <div class="o-media-object">
              <div class="o-media-object__media">
                <icon-sprite css-klassen="u-color-primary" icon-name="icon-paper-airplane" />
              </div>
              <div class="o-media-object__content">
                <span
                  ><a class="inherited" href="mailto:kundenservice@grundvers.de">kundenservice@grundvers.de</a></span
                >
              </div>
            </div>
          </div>
        </info-box>
      </zelle>
    </zeile>

    <ueberschrift v-if="!piniaStore.state.isMakler" titel="Versicherungssumme" />

    <zeile>
      <titel-zelle>
        Höhe der Ver&shy;sich&shy;er&shy;ungs&shy;summe in &euro;<br />
        <span class="u-copy-s gev19-infotext--grey">
          Empfohlen: 650 &euro; je m² Wohnfläche
          <div
            v-if="piniaStore.state.isMakler"
            class="u-text-align-left gev19__contexthelp-open"
            title="Hilfe anzeigen"
            @click="wechsleSichtbarkeit('versicherungssummeHinweisMakler')"
          />
          <div
            v-else
            class="u-text-align-left gev19__contexthelp-open"
            title="Hilfe anzeigen"
            @click="wechsleSichtbarkeit('versicherungssummeHinweisDirekt')"
          />
        </span>
      </titel-zelle>
      <zelle :breite="3">
        <input
          v-model.lazy.trim.number="piniaStore.state.tarifinformationen.versicherungssumme"
          id="versicherungssumme"
          name="versicherungssumme"
          type="number"
          @keydown="erzwingeGanzzahl"
          class="c-form-control__input"
          min="10000"
          max="2000000"
          required
          aria-required="true"
          aria-label="Höhe der Versicherungssumme"
          placeholder="€"
          data-fehlermeldung="Bitte geben Sie eine Versicherungssumme in vollen Euro (mind. 10.000 EUR) ohne Punkt und Komma ein."
          data-cy="versicherungssumme"
        />
        <validierungs-fehler :fehler-text="fehler.versicherungssumme" />
      </zelle>
    </zeile>

    <zeile>
      <zelle :breite="6" :offset="4">
        <infotext
          name="versicherungssummeHinweisMakler"
          css-klassen="o-grid__cell--no-space"
          text="Ab einer Versicherungssumme von 650 € je m<sup>2</sup> Wohnfläche erklären wir den Unterversicherungsverzicht."
        />
        <infotext
          name="versicherungssummeHinweisDirekt"
          css-klassen="o-grid__cell--no-space"
          text="Die Versicherungssumme soll dem Versicherungswert des gesamten Hausrats
        entsprechen, also dem Betrag, der für die Neuanschaffung des gesamten
        Hausrats aufgewendet werden müsste. Ist die Versicherungssumme zu
        niedrig angesetzt, liegt eine Unterversicherung vor und der Schaden wird
        nur anteilsmäßig ersetzt. Einen Abzug wegen Unterversicherung können Sie
        vermeiden, wenn Sie pro qm Wohnfläche eine Versicherungssumme von
        650 Euro vereinbaren."
        />
      </zelle>
    </zeile>

    <ueberschrift titel="Geburtsdatum" />
    <zeile>
      <titel-zelle
        >Geburtsdatum
        <div
          v-if="piniaStore.state.isMakler"
          class="u-text-align-left gev19__contexthelp-open"
          title="Hilfe anzeigen"
          @click="wechsleSichtbarkeit('geburtsdatumHinweisMakler')"
        />
      </titel-zelle>
      <zelle :breite="6">
        <input
          style="width: 50%"
          type="date"
          id="geburtsdatum"
          name="geburtsdatum"
          v-model.lazy.trim="piniaStore.state.personendaten.versicherungsnehmer.geburtsdatum"
          class="c-form-control__input"
          placeholder="TT.MM.JJJJ"
          title="Geburtsdatum im Format TT.MM.JJJJ"
          required
          aria-required="true"
          aria-label="Geburtsdatum im Format TT.MM.JJJJ"
          data-checkalter18bis100="true"
          data-cy="versicherungsnehmerGeburtstag"
        />
        <validierungs-fehler :fehler-text="fehler.geburtsdatum" />
      </zelle>
    </zeile>

    <zeile>
      <zelle :breite="6" :offset="4">
        <infotext
          name="geburtsdatumHinweisMakler"
          css-klassen="o-grid__cell--no-space"
          text="Versicherungsnehmer ab 60 Jahren erhalten in diesem Tarifrechner automatisch einen günstigeren Beitrag."
        />
      </zelle>
    </zeile>

    <zeile>
      <zelle :breite="3" :offset="4">
        <button
          @click="nextPage"
          class="c-button c-button--full-width u-mar-t-2 u-mar-b-2 mobil-mehrAbstand"
          data-cy="submitZumVergleich"
        >
          Weiter zum Vergleich
        </button>
      </zelle>
    </zeile>

    <modal
      v-if="istSichtbar('modal-AnpassungVersicherungssumme')"
      text="Mit der gewählten Versicherungssumme liegt eine Unterversicherung vor und ein Schaden wird nur
      anteilsmäßig ersetzt. Einen Abzug wegen Unterversicherung können Sie vermeiden, wenn Sie pro qm Wohnfläche
      eine Versicherungssumme von 650 Euro vereinbaren."
      titel="Anpassung Versicherungssumme"
      @closeModal="wechsleSichtbarkeit('modal-AnpassungVersicherungssumme')"
    >
      <template v-slot:inhalt>
        <zeile>
          <zelle class-override="o-grid__cell o-grid__cell--12 o-grid__cell--6-360 u-form-grid-nospace-y">
            <button class="c-button" type="button" @click="empfohleneVersicherungssummeVerwenden()">
              Weiter mit {{ formatCurrency(empfohleneVersicherungssumme) }}
            </button>
          </zelle>
          <zelle class-override="o-grid__cell o-grid__cell--12 o-grid__cell--6-360 u-form-grid-nospace-y">
            <button
              v-if="
                formatCurrency(empfohleneVersicherungssumme) !==
                formatCurrency(piniaStore.state.tarifinformationen.versicherungssumme)
              "
              class="c-button u-text-align-left"
              type="button"
              @click="gewaehlteVersicherungssummeVerwenden()"
            >
              Weiter mit
              {{ formatCurrency(piniaStore.state.tarifinformationen.versicherungssumme) }}
            </button>
          </zelle>
        </zeile>
      </template>
    </modal>

    <modal
      v-if="istSichtbar('modal-GrenzwerteUeberschritten')"
      @closeModal="wechsleSichtbarkeit('modal-GrenzwerteUeberschritten')"
      :hat-schliessen-button="true"
      titel="Online leider kein Angebot möglich"
    >
      <template v-slot:inhalt>
        <p>
          Online können wir leider kein Angebot erstellen, wenn die Wohnfläche über 300 m² liegt oder die
          Versicherungssumme mehr als 200.000 EUR beträgt.
        </p>
        <p>
          Unser Beratungsteam steht Ihnen gerne zur Verfügung und erstellt für Sie ein individuelles Angebot. Sie können
          uns von Montag bis Freitag zwischen 8:00 und 18:00 Uhr unter 040 37663-366 erreichen.
        </p>
      </template>
    </modal>
  </section>
</template>
